/**
 * EditorJsonInsert — модал «Вставить из JSON» в редакторе квизов (Фаза 12, D4).
 *
 * Textarea принимает ОДИН объект вопроса или массив объектов в формате
 * предпросмотра/экспорта (serializeDraft). Валидация живая, ДО вставки:
 * parseDraftQuestionsJSON (drafts.ts) — JSON.parse + строгий структурный
 * разбор по типам choice/poll/open/match/order/word; невалидная структура
 * физически не попадает в черновик: кнопка «Вставить» disabled, пока разбор
 * не успешен. Ошибки — инлайн с индексом элемента и полем («Вопрос 2: поле
 * body.correctIndex»). Лимит limits.questionsPerQuiz: если текущее число
 * вопросов черновика + новые > лимита — вставка блокируется отдельным
 * сообщением (лимит передаёт HostEditor — тот же источник, что у счётчика
 * N/limit). Вставка — в конец списка вопросов; свежие id назначает
 * parseDraftQuestionsJSON (genId).
 *
 * Модал portal'ится в document.body (как EditorJsonPanel, D3): position:fixed
 * внутри .card (backdrop-filter) позиционировался бы относительно карточки.
 * Доступность: role="dialog" + aria-modal, Escape/клик по оверлею закрывают.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  parseDraftQuestionsJSON,
  type DraftQuestionsParseError,
  type DraftQuestion,
} from '../../drafts';

export interface EditorJsonInsertProps {
  /** Число вопросов в ТЕКУЩЕМ черновике (проверка лимита questionsPerQuiz). */
  currentCount: number;
  /** Потолок questionsPerQuiz из GET /api/limits; undefined/0 — лимит неизвестен. */
  questionsLimit?: number;
  /** Вставка ВАЛИДИРОВАННЫХ вопросов в конец черновика. */
  onInsert: (questions: DraftQuestion[]) => void;
  onClose: () => void;
}

export function EditorJsonInsert({
  currentCount,
  questionsLimit,
  onInsert,
  onClose,
}: EditorJsonInsertProps): JSX.Element {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  // Живая валидация на каждый ввод: пустой ввод ≠ ошибка (кнопка просто
  // выключена); непустой разбирается строгим парсером drafts.ts.
  const parsed = useMemo(() => (text.trim() === '' ? null : parseDraftQuestionsJSON(text)), [text]);

  // Лимит вопросов: активен только когда лимит известен (limits загружены и > 0).
  const limit = typeof questionsLimit === 'number' && questionsLimit > 0 ? questionsLimit : null;
  const limitExceeded =
    parsed !== null &&
    parsed.ok &&
    limit !== null &&
    currentCount + parsed.questions.length > limit;
  const canInsert = parsed !== null && parsed.ok && !limitExceeded;

  useEffect(() => {
    dialogRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleSubmit(): void {
    if (parsed === null || !parsed.ok || limitExceeded) return;
    onInsert(parsed.questions);
    onClose();
  }

  /** Локализованное инлайн-сообщение по структуре ошибки разбора. */
  function errorText(error: DraftQuestionsParseError): string {
    switch (error.kind) {
      case 'parse':
        return t('host.editor.insert.errors.parse');
      case 'shape':
        return t('host.editor.insert.errors.shape');
      case 'empty':
        return t('host.editor.insert.errors.empty');
      case 'question':
        return t('host.editor.insert.errors.question', {
          index: (error.index ?? 0) + 1,
          field: error.field ?? '',
        });
    }
  }

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="editor-json-insert-overlay"
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="modal json-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('host.editor.insert.title')}
        tabIndex={-1}
        data-testid="editor-json-insert-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h3 className="modal__title">{t('host.editor.insert.title')}</h3>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            data-testid="editor-json-insert-close"
          >
            {t('host.editor.insert.close')}
          </button>
        </div>

        <div className="modal__body">
          <p className="muted small">{t('host.editor.insert.hint')}</p>
          <textarea
            className="textarea json-insert__input"
            rows={10}
            spellCheck={false}
            value={text}
            placeholder={t('host.editor.insert.placeholder')}
            onChange={(e) => setText(e.target.value)}
            aria-label={t('host.editor.insert.title')}
            data-testid="editor-json-insert-input"
          />
          {parsed !== null && !parsed.ok ? (
            <p
              className="error small json-insert__error"
              role="alert"
              data-testid="editor-json-insert-error"
            >
              {errorText(parsed.error)}
            </p>
          ) : null}
          {limitExceeded && parsed?.ok ? (
            <p
              className="error small json-insert__error"
              role="alert"
              data-testid="editor-json-insert-limit"
            >
              {t('host.editor.insert.errors.limit', {
                current: currentCount,
                extra: parsed.questions.length,
                limit,
              })}
            </p>
          ) : null}
        </div>

        <div className="json-insert__foot">
          {parsed !== null && parsed.ok ? (
            <span className="muted small" data-testid="editor-json-insert-counter">
              {limit !== null
                ? t('host.editor.insert.countWithLimit', {
                    current: currentCount,
                    extra: parsed.questions.length,
                    limit,
                  })
                : t('host.editor.insert.count', {
                    current: currentCount,
                    extra: parsed.questions.length,
                  })}
            </span>
          ) : null}
          <button
            type="button"
            className="cta json-insert__submit"
            disabled={!canInsert}
            onClick={handleSubmit}
            data-testid="editor-json-insert-submit"
          >
            {t('host.editor.insert.submit')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
